import { apiDownloadFile, apiFetch } from "@/services/apiClient";
import { buildDrillThroughParams } from "@/lib/drillThroughTableQuery";
import { appendSortParams, type SortState } from "@/lib/tableSort";
import { API_URL } from "@/lib/utils";

export interface WIPFilters {
  service_lines: string[];
  legacy_locations: string[];
  managers: string[];
  assignment_categories: string[];
  age_band_names: string[];
  date_from: string | null;
  date_to: string | null;
}

export interface WIPFilterOptions {
  service_lines: string[];
  legacy_locations: string[];
  managers: string[];
  assignment_categories: string[];
  age_band_names: string[];
  min_month: string | null;
  max_month: string | null;
}

export type WipAgeBandKey =
  | "age_0_30"
  | "age_31_60"
  | "age_61_90"
  | "age_91_120"
  | "age_120_180"
  | "age_180_plus";

export type WipDrillThroughRow = Record<string, string | number | null | undefined> & {
  total?: number | null;
};

export type WipAssignmentEntry = Record<string, string | number | null | undefined> & {
  assignment_code?: string | null;
  assignment_name?: string | null;
  grade?: string | null;
  total?: number | null;
};

export type WipClientAssignmentGroup = {
  client_code?: string | null;
  client_name?: string | null;
  assignments: WipAssignmentEntry[];
  total: Record<string, string | number | null | undefined> & { label?: string | null; total?: number | null };
};

export interface WipDrillThroughResponse {
  as_at_date: string | null;
  monthLabel: string | null;
  age_bands: Array<string | { key?: string; label?: string; name?: string }>;
  total_wip_gbp: number;
  row_count: number;
  by_assignment_manager: WipDrillThroughRow[];
  by_client_assignment: WipClientAssignmentGroup[];
  assignment_manager_total_rows: number;
  client_assignment_total_rows: number;
  data?: WipDrillThroughRow[];
}

function buildQs(f: WIPFilters): string {
  const p = new URLSearchParams();
  f.service_lines.forEach(v => p.append("service_lines", v));
  f.legacy_locations.forEach(v => p.append("legacy_locations", v));
  f.managers.forEach(v => p.append("managers", v));
  f.assignment_categories.forEach(v => p.append("assignment_categories", v));
  f.age_band_names.forEach(v => p.append("age_band_names", v));
  if (f.date_from) p.append("date_from", f.date_from);
  if (f.date_to)   p.append("date_to",   f.date_to);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await apiFetch(`${API_URL}${path}`, { method: "GET", signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const fetchWipFilterOptions = (filters: WIPFilters = { service_lines: [], legacy_locations: [], managers: [], assignment_categories: [], age_band_names: [], date_from: null, date_to: null }, signal?: AbortSignal) =>
  getJson<Omit<WIPFilterOptions, "min_month" | "max_month">>(`/wip/filter-options${buildQs(filters)}`, signal);

export const fetchWipDateRange = () =>
  getJson<{ min_month: string | null; max_month: string | null }>("/wip/date-range");

export const fetchWipMonthly = (f: WIPFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; total_wip_gbp: number }[] }>(`/wip/monthly${buildQs(f)}`, signal);

export const fetchWipMonthlyByAgeBand = (f: WIPFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[] }>(`/wip/monthly-by-age-band${buildQs(f)}`, signal);

export const fetchWipRiskConcentration = (f: WIPFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; risk_pct: number }[] }>(`/wip/risk-concentration${buildQs(f)}`, signal);

export const fetchWipByOfficeAgeBand = (f: WIPFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[] }>(`/wip/by-office-age-band${buildQs(f)}`, signal);

export const fetchWipByServiceLine = (f: WIPFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[]; series: string[] }>(`/wip/by-service-line${buildQs(f)}`, signal);

export const fetchWipByLocation = (f: WIPFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[]; series: string[] }>(`/wip/by-location${buildQs(f)}`, signal);

export const fetchWipTopManagers = (f: WIPFilters, limit = 10, signal?: AbortSignal) =>
  getJson<{ data: { manager: string; wip_gbp: number }[] }>(
    `/wip/top-managers${buildQs(f)}${buildQs(f) ? "&" : "?"}limit=${limit}`, signal);

export const fetchWipAvgAge = (f: WIPFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; avg_age_days: number }[] }>(`/wip/avg-age${buildQs(f)}`, signal);

export const WIP_DRILL_FILTER_KEYS = [
  "service_lines",
  "legacy_locations",
  "managers",
  "assignment_categories",
  "age_band_names",
] as const;

export const fetchWipDrillThrough = (
  month: string,
  filters?: Partial<WIPFilters> & { managerSort?: SortState; clientSort?: SortState },
  signal?: AbortSignal,
  page?: { limit: number; skip: number },
) => {
  const { managerSort, clientSort, ...baseFilters } = filters ?? {};
  const params = buildDrillThroughParams({
    month,
    filters: baseFilters as Record<string, string[] | string | null | undefined>,
    filterKeys: [...WIP_DRILL_FILTER_KEYS],
  });
  if (page) {
    params.set("limit", String(page.limit));
    params.set("skip", String(page.skip));
  }
  if (managerSort) {
    appendSortParams(params, managerSort, { sortByParam: "manager_sort_by", sortDirParam: "manager_sort_dir" });
  }
  if (clientSort) {
    appendSortParams(params, clientSort, { sortByParam: "client_sort_by", sortDirParam: "client_sort_dir" });
  }
  return getJson<WipDrillThroughResponse>(`/wip/drill-through?${params.toString()}`, signal);
};

export const downloadWipDrillThroughExcel = (
  month: string,
  filters?: Partial<WIPFilters>,
  exportOptions: Array<"assignment_manager" | "client_assignment"> = [],
) => {
  const params = buildDrillThroughParams({
    month,
    filters: filters as Record<string, string[] | string | null | undefined>,
    filterKeys: [...WIP_DRILL_FILTER_KEYS],
  });
  exportOptions.forEach((option) => params.append("export_options", option));
  return apiDownloadFile(
    `${API_URL}/wip/drill-through/export?${params.toString()}`,
    exportOptions.length > 1 ? "WIP Drill Through Exports.zip" : "wip-drill-through.xlsx",
  );
};
